import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from '../../contexts/UserContext';
import { isStudioUseMock } from '../config';
import {
  applyMockAuthQueryParam,
  getMockAuthState,
  setMockAuthState,
} from '../api/mockState';
import type { StudioMockAuthState } from '../api/types';

export type StudioSessionStatus =
  | 'loading'
  | 'logged_out'
  | 'linked'
  | 'stale_link';

/** Hung `/api/me` must not leave Studio on an eternal spinner. */
export const STUDIO_AUTH_RESOLVE_MS = 8000;

/**
 * Studio auth model: scoutlytics_session (via useUser) + Twitch link liveness.
 * In mock mode the in-memory mock state drives the three explicit states.
 * Twitch-link staleness is also signaled by 401 from `/api/rtts/*` data calls.
 */
export function useStudioAuth() {
  const { user, loading, isAuthenticated, authError, refreshUser } = useUser();
  const [, bump] = useState(0);
  const [resolveTimedOut, setResolveTimedOut] = useState(false);

  const refreshMock = useCallback(() => {
    bump((n) => n + 1);
  }, []);

  const applyQueryMock = useCallback(
    (search: string) => {
      const applied = applyMockAuthQueryParam(search);
      if (applied) refreshMock();
      return applied;
    },
    [refreshMock]
  );

  const setMockState = useCallback(
    (state: StudioMockAuthState) => {
      setMockAuthState(state);
      refreshMock();
    },
    [refreshMock]
  );

  const mockState = getMockAuthState();

  const status: StudioSessionStatus = useMemo(() => {
    if (isStudioUseMock()) {
      return mockState;
    }
    if (loading) return 'loading';
    if (!isAuthenticated || !user) return 'logged_out';
    // Live Twitch-link liveness is not knowable from the cookie alone —
    // treat as linked until a 401 from /api/rtts/* flips UI to reconnect.
    return 'linked';
  }, [mockState, loading, isAuthenticated, user]);

  useEffect(() => {
    if (status !== 'loading') {
      setResolveTimedOut(false);
      return;
    }
    const id = window.setTimeout(() => {
      setResolveTimedOut(true);
    }, STUDIO_AUTH_RESOLVE_MS);
    return () => window.clearTimeout(id);
  }, [status]);

  const retryAuth = useCallback(async () => {
    setResolveTimedOut(false);
    await refreshUser();
  }, [refreshUser]);

  return {
    status,
    isAuthenticated: status === 'linked' || status === 'stale_link',
    isLoggedOut: status === 'logged_out',
    needsTwitchReconnect: status === 'stale_link',
    loading: status === 'loading' && !resolveTimedOut,
    resolveTimedOut,
    authError: authError || null,
    retryAuth,
    user,
    applyQueryMock,
    setMockState,
    refreshMock,
  };
}

export type { StudioMockAuthState };
